// qs-useCanvasPerformance.ts
import type { KonvaStage } from '@/types/qs-type-konva'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { ref } from 'vue'

const RENDER_THRESHOLD_FAST = 16.67
const RENDER_THRESHOLD_SLOW = 50

export function useCanvasPerformance() {
  const canvasStore = useCanvasStore()

  const isRendering = ref(false)
  const renderQueue = ref<Array<() => void>>([])
  const lastRenderTime = ref(0)
  const adaptiveThrottle = ref(16)
  const renderScheduled = ref(false)

  const shouldThrottleRender = (): boolean => {
    const now = performance.now()
    if (now - lastRenderTime.value < adaptiveThrottle.value) {
      return true
    }
    lastRenderTime.value = now
    return false
  }

  const updateAdaptiveThrottle = (): void => {
    const now = performance.now()
    const deltaTime = now - lastRenderTime.value

    if (deltaTime > RENDER_THRESHOLD_SLOW) {
      adaptiveThrottle.value = Math.min(adaptiveThrottle.value + 2, 50)
    } else if (deltaTime < RENDER_THRESHOLD_FAST) {
      adaptiveThrottle.value = Math.max(adaptiveThrottle.value - 1, 8)
    }
  }

  const scheduleRender = (callback: () => void): void => {
    if (renderScheduled.value) {
      renderQueue.value.push(callback)
      return
    }

    renderScheduled.value = true
    requestAnimationFrame(() => {
      callback()

      if (renderQueue.value.length > 0) {
        const nextCallback = renderQueue.value.shift()
        if (nextCallback) {
          scheduleRender(nextCallback)
        }
      }

      renderScheduled.value = false
    })
  }

  const clearStageCaches = (): void => {
    if (canvasStore.stage) {
      canvasStore.stage.clearCache()
    }
  }

  const optimizeStageRendering = (stage: KonvaStage | null): void => {
    if (!stage) return
    stage.cache()
    stage.batchDraw()
  }

  const optimizeForLargeDrawings = (stage: KonvaStage | null): void => {
    if (!stage) return
    stage.pixelRatio(Math.min(window.devicePixelRatio, 2))
    stage.hitGraphEnabled(false)
  }

  const optimizeForHighZoom = (stage: KonvaStage | null, scale: number): void => {
    if (!stage) return

    if (scale > 4) {
      stage.pixelRatio(1)
    } else if (scale > 2) {
      stage.pixelRatio(Math.min(window.devicePixelRatio, 1.5))
    } else {
      stage.pixelRatio(window.devicePixelRatio)
    }
  }

  const manageMemory = (): void => {
    clearStageCaches()
    if (window.gc) {
      window.gc()
    }
  }

  const setupPerformanceMonitoring = (): void => {
    adaptiveThrottle.value = 16
    lastRenderTime.value = performance.now()
  }

  const cleanupPerformanceMonitoring = (): void => {
    renderQueue.value = []
    renderScheduled.value = false
    clearStageCaches()
  }

  const getPerformanceMetrics = () => {
    return {
      isRendering: isRendering.value,
      renderQueueLength: renderQueue.value.length,
      adaptiveThrottle: adaptiveThrottle.value,
      lastRenderTime: lastRenderTime.value
    }
  }

  return {
    isRendering,
    adaptiveThrottle,
    shouldThrottleRender,
    updateAdaptiveThrottle,
    scheduleRender,
    optimizeStageRendering,
    clearStageCaches,
    optimizeForLargeDrawings,
    optimizeForHighZoom,
    manageMemory,
    setupPerformanceMonitoring,
    cleanupPerformanceMonitoring,
    getPerformanceMetrics
  }
}